
import React from 'react';
import AppLayout from '@/components/app/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MessageCircle, Phone, Mail, HelpCircle, Clock, MapPin } from 'lucide-react';

const AppConcierge = () => {
  const faqs = [
    {
      question: "How do JUV Credits work?",
      answer: "JUV Credits are your digital currency for treatments. Each credit is worth $50, and they never expire while your membership is active."
    },
    {
      question: "Can I reschedule my appointment?",
      answer: "Yes! You can reschedule up to 24 hours before your appointment without any fees."
    },
    {
      question: "What happens if I cancel my membership?",
      answer: "Your unused credits will be converted to cash value and can be used for 12 months after cancellation."
    },
    {
      question: "How do I refer a friend?",
      answer: "Use your unique referral code in the app. You'll both get 2 bonus credits when they complete their first treatment."
    }
  ];

  return (
    <AppLayout>
      <div className="px-4 py-6 space-y-6">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-juv-deep-chestnut mb-2">Concierge</h1>
          <p className="text-gray-600">We're here to help with anything you need</p>
        </div>

        {/* Quick Contact Options */}
        <div className="grid grid-cols-1 gap-4">
          <Card className="bg-gradient-to-r from-juv-terracotta to-juv-deep-chestnut text-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <MessageCircle className="w-8 h-8" />
                <div className="flex-1">
                  <h3 className="font-semibold mb-1">Live Chat Support</h3>
                  <p className="text-white/80 text-sm">Get instant help from our team</p>
                </div>
                <Button variant="secondary">
                  Chat Now
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <Phone className="w-6 h-6 text-juv-terracotta" />
                <div className="flex-1">
                  <h3 className="font-semibold text-juv-deep-chestnut">Call My Studio</h3>
                  <p className="text-gray-600 text-sm">Speak directly with your local team</p>
                </div>
                <Button variant="outline" size="sm">
                  Call
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <Mail className="w-6 h-6 text-juv-terracotta" />
                <div className="flex-1">
                  <h3 className="font-semibold text-juv-deep-chestnut">Email Support</h3>
                  <p className="text-gray-600 text-sm">support@juvmedspa.com</p>
                </div>
                <Button variant="outline" size="sm">
                  Email
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Business Hours */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-juv-deep-chestnut">
              <Clock className="w-5 h-5" />
              Business Hours
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Monday - Friday</span>
                <span>8:00 AM - 7:00 PM</span>
              </div>
              <div className="flex justify-between">
                <span>Saturday</span>
                <span>9:00 AM - 5:00 PM</span>
              </div>
              <div className="flex justify-between">
                <span>Sunday</span>
                <span>10:00 AM - 4:00 PM</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* FAQ Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-juv-deep-chestnut">
              <HelpCircle className="w-5 h-5" />
              Frequently Asked Questions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="border-b border-gray-100 pb-4 last:border-b-0">
                  <h4 className="font-medium text-juv-deep-chestnut mb-2">{faq.question}</h4>
                  <p className="text-sm text-gray-600">{faq.answer}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Find Your Studio */}
        <Card className="bg-juv-soft-blush">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <MapPin className="w-8 h-8 text-juv-terracotta" />
              <div className="flex-1">
                <h3 className="font-semibold text-juv-deep-chestnut mb-1">Find Your Nearest Studio</h3>
                <p className="text-gray-600 text-sm">
                  Get location-specific hours and contact information
                </p>
              </div>
              <Button className="bg-juv-terracotta hover:bg-juv-deep-chestnut">
                Find Studio
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Emergency Contact */}
        <Card className="border-juv-rose-clay border-2">
          <CardContent className="p-4 text-center">
            <h3 className="font-semibold text-juv-deep-chestnut mb-2">Post-Treatment Emergency</h3>
            <p className="text-sm text-gray-600 mb-4">
              If you're experiencing an emergency related to your recent treatment, contact us immediately.
            </p>
            <Button variant="outline" className="border-juv-terracotta text-juv-terracotta">
              Emergency Contact: (555) 123-4567
            </Button>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};

export default AppConcierge;
